import { useMemo, useState } from "react";
import FeatureContext from "./FeatureContext";
import {
  FLAG_KEYS,
  FLAG_ENTITIES,
  RELEASE_STAGES,
  POPULI_ORG_ID,
} from "core/constants";
import { PROVIDER_LICENSE_CONFIGS } from "../constants";
import { evaluateFlag } from "core/hooks/featureFlagging";
import {
  useAnalyticsHubFlagDependencies,
  useManagedProvidersFlagDependencies,
} from "core/hooks/featureFlagDependencies";
import useActiveMembership from "core/hooks/useActiveMembership";

/**
 * Data-dependent predicate for the Analytics Hub flag (runs after the stage
 * gate). Populi may view an empty Analytics Hub; every other org needs at least
 * one dashboard (standard OR custom workbook). Returns false while the workbook
 * queries are still loading (undefined), then flips true reactively once they
 * resolve — mirroring isManagedProvidersLicensed.
 */
export const isAnalyticsHubEnabled = ({
  standardWorkbooks,
  customWorkbooks,
  isPopuliOrg,
}) => {
  if (isPopuliOrg) {
    return true;
  }

  return (
    (standardWorkbooks?.length ?? 0) > 0 || (customWorkbooks?.length ?? 0) > 0
  );
};

export const isManagedProvidersLicensed = ({ license, catalog }) => {
  if (!license || !catalog) {
    return false;
  }

  const licensedProductIds = Object.keys(license.products).filter(
    (productId) => {
      return license.products[productId]?.enabled && catalog[productId];
    }
  );
  const catalogProductsLicensed = licensedProductIds.map(
    (productId) => catalog[productId]
  );
  const allLicensedProductsAndDependencies = catalogProductsLicensed.reduce(
    (finalList, product) => {
      return finalList.concat(product.id).concat(product.dependencies);
    },
    []
  );

  const hasProviderAlias = allLicensedProductsAndDependencies.includes(
    PROVIDER_LICENSE_CONFIGS.ALIAS
  );
  const hasProviderRelationship = allLicensedProductsAndDependencies.includes(
    PROVIDER_LICENSE_CONFIGS.RELATIONSHIP
  );

  return hasProviderAlias && hasProviderRelationship;
};

const configuredAnalyticsHubStage = import.meta.env
  .REACT_APP_ANALYTICS_HUB_STAGE;
const ANALYTICS_HUB_STAGE = Object.values(RELEASE_STAGES).includes(
  configuredAnalyticsHubStage
)
  ? configuredAnalyticsHubStage
  : RELEASE_STAGES.BETA;

const FLAGS = {
  [FLAG_KEYS.MANAGED_PROVIDERS]: {
    key: FLAG_KEYS.MANAGED_PROVIDERS,
    description: "Provides access to managed providers feature.",
    entity: FLAG_ENTITIES.ORGANIZATION,
    stage: RELEASE_STAGES.GA,
    isEnabled: isManagedProvidersLicensed,
  },
  [FLAG_KEYS.ANALYTICS_HUB]: {
    key: FLAG_KEYS.ANALYTICS_HUB,
    description: "Provides access to the Analytics Hub feature.",
    entity: FLAG_ENTITIES.ORGANIZATION,
    stage: ANALYTICS_HUB_STAGE,
    isEnabled: isAnalyticsHubEnabled,
  },
};

// Example Context
// {
//   "organization": {
//     key: "populi",
//     availableStages: [RELEASE_STAGES.BETA, RELEASE_STAGES.GA]
//   }
// };

/**
 * Owns feature flags end to end: the flag definitions above, the entity context
 * they are evaluated against, and the resolved result for each flag.
 *
 * Because data-dependent flags need per-organization data, this must mount
 * inside ActiveMembershipProvider (see App.jsx) — currentUser and
 * activeMembership have to exist before any dependency query can run.
 *
 * Every dependency hook below is called unconditionally and in a fixed order, so
 * the rules of hooks hold no matter which route is active. That is what lets
 * consumers evaluate a flag by a variable key (e.g. the matched route's flag)
 * without branching per flag.
 *
 * To add a data-dependent flag: give it an `isEnabled` predicate in FLAGS, then
 * add its dependency hook and one entry to `dependenciesByFlag`. Consumers do
 * not change.
 */
const FeatureProvider = ({ children }) => {
  // Not set today, but reserved for if the API develops flagging endpoints.
  const [flags, setFlags] = useState(FLAGS);
  const { activeMembership } = useActiveMembership();

  const organizationId = activeMembership?.id;
  const isPopuliOrg = organizationId === POPULI_ORG_ID;

  // Derived rather than stored: the entity context follows directly from the
  // active membership, so there is nothing to synchronize and it is correct on
  // the first render — which matters, because it gates whether a flag's
  // dependencies are fetched at all.
  const context = useMemo(
    () => ({
      [FLAG_ENTITIES.ORGANIZATION]: {
        entity: FLAG_ENTITIES.ORGANIZATION,
        key: organizationId ?? "default",
        availableStages: isPopuliOrg
          ? [RELEASE_STAGES.BETA, RELEASE_STAGES.GA]
          : [RELEASE_STAGES.GA],
      },
    }),
    [organizationId, isPopuliOrg]
  );

  const hasStageAccess = (flagKey) => {
    const flag = flags?.[flagKey];

    return (
      !!flag && !!context[flag.entity]?.availableStages.includes(flag.stage)
    );
  };

  // One line per data-dependent flag — the only place that grows.
  const analyticsHub = useAnalyticsHubFlagDependencies({
    organizationId,
    enabled: hasStageAccess(FLAG_KEYS.ANALYTICS_HUB),
  });
  const managedProviders = useManagedProvidersFlagDependencies({
    organizationId,
    enabled: hasStageAccess(FLAG_KEYS.MANAGED_PROVIDERS),
  });

  const resolvedFlags = useMemo(() => {
    const dependenciesByFlag = {
      [FLAG_KEYS.ANALYTICS_HUB]: analyticsHub,
      [FLAG_KEYS.MANAGED_PROVIDERS]: managedProviders,
    };

    // Context every predicate receives for free — no per-flag boilerplate.
    const baseDeps = { activeMembership, organizationId, isPopuliOrg };

    return Object.values(flags ?? {}).reduce((resolved, flag) => {
      const { deps = {}, isResolving = false } =
        dependenciesByFlag[flag.key] ?? {};

      resolved[flag.key] = {
        isEnabled: evaluateFlag(flag, context[flag.entity], {
          ...baseDeps,
          ...deps,
        }),
        isResolving,
      };

      return resolved;
    }, {});
  }, [
    flags,
    context,
    analyticsHub,
    managedProviders,
    activeMembership,
    organizationId,
    isPopuliOrg,
  ]);

  const value = useMemo(
    () => ({ flags, setFlags, context, resolvedFlags }),
    [flags, context, resolvedFlags]
  );

  return (
    <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>
  );
};

export default FeatureProvider;
